import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { ActivityLog } from '../../types';

const AdminActivity: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState<string>('all');

  useEffect(() => {
    loadLogs();
  }, [page, actionFilter]);

  const loadLogs = async () => {
    setLoading(true);
    const response = await api.getActivityLogs({
      page, pageSize: 30,
      action: actionFilter !== 'all' ? actionFilter : undefined
    });
    if (response.success && response.data) {
      setLogs(response.data.data);
      setTotalPages(response.data.totalPages);
    }
    setLoading(false);
  };

  const actionTypes = ['all', 'LOGIN', 'LOGOUT', 'USER_CREATED', 'USER_APPROVED', 'USER_SUSPENDED', 'PASSWORD_CHANGED'];

  const getActionColor = (action: string) => {
    if (action.includes('LOGIN')) return 'text-blue-400 bg-blue-500/20';
    if (action.includes('CREATED') || action.includes('APPROVED')) return 'text-emerald-400 bg-emerald-500/20';
    if (action.includes('SUSPENDED') || action.includes('DELETED')) return 'text-red-400 bg-red-500/20';
    if (action.includes('UPDATED') || action.includes('CHANGED')) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-neutral-400 bg-neutral-500/20';
  };

  if (loading && logs.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Activity Logs</h1>
        <p className="text-neutral-500">Monitor user actions and system events</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {actionTypes.map(action => (
          <button key={action} onClick={() => { setActionFilter(action); setPage(1); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${
              actionFilter === action ? 'bg-emerald-600 text-white' : 'bg-[#1a1a1a] text-neutral-400 hover:bg-[#262626]'
            }`}>
            {action === 'all' ? 'All' : action.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        {logs.length > 0 ? (
          <>
            <div className="divide-y divide-[#262626]">
              {logs.map(log => (
                <div key={log.id} className="p-4 hover:bg-[#1a1a1a]">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <div>
                        <p className="text-white">
                          <span className="text-neutral-400">User:</span> {log.userId?.substring(0, 8) || 'System'}...
                          {log.actorId && log.actorId !== log.userId && (
                            <span className="text-neutral-500 ml-2">by {log.actorId.substring(0, 8)}...</span>
                          )}
                        </p>
                        {log.resourceType && (
                          <p className="text-neutral-500 text-sm">
                            {log.resourceType}: {log.resourceId?.substring(0, 8)}...
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-neutral-500 text-sm whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString('en-BD')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="p-4 border-t border-[#262626] flex items-center justify-between">
                <p className="text-neutral-500 text-sm">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 bg-[#1a1a1a] text-white rounded-lg disabled:opacity-50">Previous</button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 bg-[#1a1a1a] text-white rounded-lg disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-neutral-500">No activity logs found</div>
        )}
      </div>
    </div>
  );
};

export default AdminActivity;
